import AnimationController from '../../../assets/js/controllers/animation';
import ResizeController from '../../../assets/js/controllers/resize';

import ParallaxEvent from '../../../components/parallax/parallax-event';
import Video from '../../../components/video/script';

import { classesActiveAndIn, classesDelayedSwap, multiAddEventListener, multiRemoveEventListener } from '../../../assets/js/helpers';

// constants
import CursorEvent from '../../../bits/cursor/cursor-event';
import Platform from '../../../assets/js/constants/platform';
import SkrolliteEvent from '../../../components/skrollite/skrollite-event';
import SliderDraggableEvent from '../../../components/slider-draggable/slider-draggable-event';
// import VideoEvent from '../../../assets/js/constants/video-event';

export default class HeroHome {

	static selector = 'hero-home';

	// class variables

	components = {
		videos: []
	};

	controllers = {
		animation: null,
		resize: null
	};

	coords = {
		contentWrapper: {
			height: null
		}
	};

	el = {};

	flags = {
		isCursorEnabled: true
	};

	states = {
		activeIndex: 0,
		activeMetaItem: null,
		activeVideo: null,
		activeThumbnail: null,
		activeThumbnailProgress: null,
		total: 0
	};

	timeouts = {
		meta: null
	};

	// constructor

	constructor(container) {
		this.el = {
			container,

			// singles
			contentMeta: container.querySelector(`.${this.constructor.selector}__features-content-meta`),
			contentWrapper: container.querySelector(`.${this.constructor.selector}__features`),
			cursor: container.querySelector(`.${this.constructor.selector}__features-cursor`),
			sidebar: container.querySelector(`.${this.constructor.selector}__features-sidebar`),
			sidebarIndex: container.querySelector(`.${this.constructor.selector}__features-sidebar-progress-index`),
			slider: container.querySelector('[data-slider-draggable]'),

			// arrays
			contentMetaItems: container.querySelectorAll(`.${this.constructor.selector}__features-content-meta-item`),
			parallaxes: container.querySelectorAll(`.${this.constructor.selector}__features-sticky-content div[data-parallax]`),
			thumbnails: container.querySelectorAll(`.${this.constructor.selector}__features-content-thumbnail`),
			videos: container.querySelectorAll(`.${this.constructor.selector}__features-sticky-content-item-video`),
			videoWrappers: container.querySelectorAll(`.${this.constructor.selector}__features-sticky-content-item-video-wrapper`)
		};

		this.states.total = this.el.videos.length;

		this.initialize();
	}

	// initialize

	initialize() {
		this.#addVideos();
		this.#addEventListeners();
		this.renderActiveItemByIndex(0);
	}

	// add and remove listeners

	#addEventListeners = () => {
		// hide cursor when hovering over left text
		this.el.contentMeta.addEventListener('mouseenter', this.#onMetaOver);
		this.el.contentMeta.addEventListener('mouseleave', this.#onMetaOut);

		// when offscreen pause current video
		this.el.contentWrapper.addEventListener(SkrolliteEvent.WINDOW_IN, this.#onContentWrapperWindowIn);
		this.el.contentWrapper.addEventListener(SkrolliteEvent.WINDOW_OUT, this.#onContentWrapperWindowOut);

		// check whether clicking anywhere in slider area will trigger an edge slide change
		this.el.slider.addEventListener('click', this.#onSliderClick);

		// listen for changes in the slider
		this.el.slider.addEventListener(SliderDraggableEvent.SEEKING, this.#onSliderSeeking);
		this.el.slider.addEventListener(SliderDraggableEvent.DRAG_START, this.#onSliderDragStart);

		// listen for thumbnail interaction
		multiAddEventListener(this.el.thumbnails, 'click', this.#onThumbnailClick);
		multiAddEventListener(this.el.thumbnails, 'mouseenter', this.#onThumbnailOver);
		multiAddEventListener(this.el.thumbnails, 'mouseleave', this.#onThumbnailOut);

		// listen for video player state changes
		// multiAddEventListener(this.el.videos, VideoEvent.LOADING_SUCCESS, this.#onVideoLoad);
		// multiAddEventListener(this.el.videoWrappers, VideoEvent.PLAYING, this.#onVideoPlay);

		// animation ticking to update video progress bar
		this.controllers.animation = AnimationController.set({
			update: this.#onResizeUpdate,
			render: this.#onAnimationRender
		});

		this.controllers.resize = ResizeController.set({
			update: this.#onResizeUpdate,
			render: this.#onResizeRender
		});

		this.measureResize();
		this.adjustResize();
	}

	#removeEventListeners = () => {
		this.el.contentMeta.removeEventListener('mouseenter', this.#onMetaOver);
		this.el.contentMeta.removeEventListener('mouseleave', this.#onMetaOut);

		this.el.contentWrapper.removeEventListener(SkrolliteEvent.WINDOW_IN, this.#onContentWrapperWindowIn);
		this.el.contentWrapper.removeEventListener(SkrolliteEvent.WINDOW_OUT, this.#onContentWrapperWindowOut);

		this.el.slider.removeEventListener('click', this.#onSliderClick);
		this.el.slider.removeEventListener(SliderDraggableEvent.SEEKING, this.#onSliderSeeking);
		this.el.slider.removeEventListener(SliderDraggableEvent.DRAG_START, this.#onSliderDragStart);

		multiRemoveEventListener(this.el.thumbnails, 'click', this.#onThumbnailClick);
		multiRemoveEventListener(this.el.thumbnails, 'mouseenter', this.#onThumbnailOver);
		multiRemoveEventListener(this.el.thumbnails, 'mouseleave', this.#onThumbnailOut);

		// multiRemoveEventListener(this.el.videos, VideoEvent.LOADING_SUCCESS, this.#onVideoLoad);
		// multiRemoveEventListener(this.el.videoWrappers, VideoEvent.PLAYING, this.#onVideoPlay);

		AnimationController.clear(this.controllers.animation);
		ResizeController.clear(this.controllers.resize);
	}

	// listener methods

	#onMetaOver = () => {
		this.cursorHide();
	}

	#onMetaOut = () => {
		this.cursorShow();
	}

	#onContentWrapperWindowIn = () => {
		this.activeVideoPlay();
	}

	#onContentWrapperWindowOut = () => {
		this.activeVideoPause();
	}

	#onSliderClick = () => {
		if (this.flags.isCursorEnabled) {
			if (this.el.cursor.classList.contains('is-edge-left')) {
				this.el.slider.dispatchEvent(new Event(SliderDraggableEvent.PREV));
			} else if (this.el.cursor.classList.contains('is-edge-right')) {
				this.el.slider.dispatchEvent(new Event(SliderDraggableEvent.NEXT));
			}
		}
	}

	#onSliderSeeking = (e) => {
		this.renderActiveItemByIndex(e.detail.index);
	}

	#onSliderDragStart = () => {
		for (let i = 0; i < this.components.videos.length; i++) {
			const video = this.components.videos[i];
			video.pause();
		}

		for (let i = 0; i < this.el.parallaxes.length; i++) {
			const $parallax = this.el.parallaxes[i];
			if (i !== this.states.index) {
				$parallax.dispatchEvent(new Event(ParallaxEvent.RENDER));
			}
		}
	}

	#onThumbnailClick = (e) => {
		const $target = e.currentTarget;
		this.setActiveItemByIndex($target.dataset.index);
	}

	#onThumbnailOver = () => {
		this.cursorHide();
	}

	#onThumbnailOut = () => {
		this.cursorShow();
	}

	// controller methods

	#onAnimationUpdate = () => this.states.activeVideo;

	#onAnimationRender = () => {
		if (this.states.activeVideo) {
			if (this.states.activeVideo.ratio >= 1) {
				this.nextVideo();
			}
			this.states.activeThumbnailProgress.style.transform = `scaleX(${this.states.activeVideo.ratio})`;
		}
	}

	#onResizeUpdate = () => {
		this.measureResize();
		return true;
	}

	#onResizeRender = () => {
		this.adjustResize();
	}

	//

	measureResize() {
		this.coords.contentWrapper.height = this.el.contentWrapper.offsetHeight;
	}

	adjustResize() {
		if (ResizeController.platform === Platform.DESKTOP) {
			this.el.sidebar.style.width = `${this.coords.contentWrapper.height}px`;
		} else {
			this.el.sidebar.style.width = '';
		}
	}

	// change active item

	setActiveItemByIndex(index) {
		if (!isNaN(parseInt(index))) {
			this.el.slider.dispatchEvent(new CustomEvent(SliderDraggableEvent.SEEK, { detail: { index } }));
		}
	}

	renderActiveItemByIndex(index) {
		if (!isNaN(parseInt(index))) {

			const lastIndex = this.states.index;
			const $lastActiveMetaItem = this.states.activeMetaItem;
			const $lastActiveThumbnail = this.states.activeThumbnail;
			const $lastActiveThumbnailProgress = this.states.activeThumbnailProgress;
			const lastActiveVideo = this.states.activeVideo;

			const isFirst = !lastActiveVideo;

			this.states.index = parseInt(index);
			this.states.activeMetaItem = this.el.contentMetaItems[index];
			this.states.activeThumbnail = this.el.thumbnails[index];
			this.states.activeThumbnailProgress = this.states.activeThumbnail.querySelector(`.${this.constructor.selector}__features-content-thumbnail-underline`);
			this.states.activeVideo = this.components.videos[index];

			if (this.states.index !== lastIndex || isFirst) {

				this.el.sidebarIndex.innerHTML = this.states.index <= 9 ? `0${(this.states.index + 1)}` : this.states.index + 1;

				for (let i = 0; i < this.components.videos.length; i++) {
					const video = this.components.videos[i];
					if (video !== this.states.activeVideo) {
						video.pause();
					}
				}

				for (let i = 0; i < this.el.parallaxes.length; i++) {
					const $parallax = this.el.parallaxes[i];
					if (i !== this.states.index) {
						$parallax.dispatchEvent(new Event(ParallaxEvent.DISABLE));
					}
				}

				if (!isFirst) {
					// reset thumbnail progress indicator
					$lastActiveThumbnail.classList.remove('is-active');
					$lastActiveThumbnailProgress.style.transform = 'scaleX(0)';

					// transition meta in and out
					classesDelayedSwap(this.states.activeMetaItem, $lastActiveMetaItem, 200);
				} else {
					classesActiveAndIn(this.states.activeMetaItem);
				}

				if (this.states.index === 0) {
					this.el.cursor.dispatchEvent(new Event(CursorEvent.DISABLE_LEFT));
				} else {
					this.el.cursor.dispatchEvent(new Event(CursorEvent.ENABLE_LEFT));
				}

				if (this.states.index === this.states.total - 1) {
					this.el.cursor.dispatchEvent(new Event(CursorEvent.DISABLE_RIGHT));
				} else {
					this.el.cursor.dispatchEvent(new Event(CursorEvent.ENABLE_RIGHT));
				}

				this.el.parallaxes[this.states.index].dispatchEvent(new Event(ParallaxEvent.ENABLE));

				this.states.activeThumbnail.classList.add('is-active');
			}

			// load video if not already loaded

			this.states.activeVideo.play();
		}
	}

	nextVideo() {
		let nextIndex = this.states.index + 1;
		if (nextIndex >= this.states.total) {
			nextIndex = 0;
		}
		this.setActiveItemByIndex(nextIndex);
	}

	// cursor

	cursorHide() {
		this.flags.isCursorEnabled = false;
		this.el.cursor.dispatchEvent(new Event(CursorEvent.HIDE));
	}

	cursorShow() {
		this.flags.isCursorEnabled = true;
		this.el.cursor.dispatchEvent(new Event(CursorEvent.SHOW));
	}

	// video

	#onVideoLoadStart = () => {
		if (this.states.activeVideo.video.src) {
			this.states.activeVideo.video.removeEventListener('loadstart', this.#onVideoLoadStart);
			this.states.activeVideo.play();
		}
	}

	#addVideos = () => {
		this.#removeVideos();
		for (let i = 0; i < this.el.videos.length; i++) {
			const $video = this.el.videos[i];
			const video = new Video($video);
			video.load(false);
			this.components.videos.push(video);
		}
	}

	#removeVideos = () => {
		if (this.components.videos.length) {
			for (let i = 0; i < this.components.videos.length; i++) {
				const video = this.components.videos[i];
				video.destroy();
			}
			this.components.videos = [];
		}
	}

	activeVideoPause() {
		if (this.states.activeVideo) {
			this.states.activeVideo.pause();
		}
	}

	activeVideoPlay() {
		if (this.states.activeVideo) {
			this.states.activeVideo.play();
		}
	}

	// destroy

	destroy() {
		this.#removeVideos();
		this.#removeEventListeners();
	}

}
